import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { useClientProfile } from './ClientProfileContext';

const AmazonProductsContext = createContext();

export const useAmazonProducts = () => {
  const context = useContext(AmazonProductsContext);
  if (!context) throw new Error('useAmazonProducts must be used within AmazonProductsProvider');
  return context;
};

export const AmazonProductsProvider = ({ children }) => {
  const { isVendr } = useAuth();
  const { profile, loading: profileLoading } = useClientProfile();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProducts = useCallback(async () => {
    if (!profile?.id || !isVendr) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('amazon_products')
        .select('*')
        .eq('client_profile_id', profile.id)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setProducts(data ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, isVendr]);

  useEffect(() => {
    if (!profileLoading) loadProducts();
  }, [profileLoading, loadProducts]);

  const saveProduct = async (values, productId, shouldSubmit = false) => {
    if (!profile?.id) return { success: false, error: 'No profile' };
    const payload = {
      ...values,
      status: shouldSubmit ? 'submitted' : 'draft',
      updated_at: new Date().toISOString(),
    };
    try {
      if (productId) {
        const { data, error: updateError } = await supabase
          .from('amazon_products')
          .update(payload)
          .eq('id', productId)
          .select()
          .single();
        if (updateError) return { success: false, error: updateError.message };
        setProducts(prev => prev.map(p => (p.id === productId ? data : p)));
        return { success: true, data };
      }
      const { data, error: insertError } = await supabase
        .from('amazon_products')
        .insert([{ ...payload, client_profile_id: profile.id }])
        .select()
        .single();
      if (insertError) return { success: false, error: insertError.message };
      setProducts(prev => [data, ...prev]);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const { error: delError } = await supabase
        .from('amazon_products')
        .delete()
        .eq('id', productId);
      if (delError) return { success: false, error: delError.message };
      setProducts(prev => prev.filter(p => p.id !== productId));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const bulkImportProducts = async (rows) => {
    if (!profile?.id) return { success: false, error: 'No profile' };
    const now = new Date().toISOString();
    const errors = [];
    let imported = 0;

    for (const row of rows) {
      const { __rowNumber, sku, ...rest } = row;
      const payload = {
        ...rest,
        sku,
        client_profile_id: profile.id,
        status: 'draft',
        updated_at: now,
      };

      try {
        const { data: existing } = await supabase
          .from('amazon_products')
          .select('id')
          .eq('client_profile_id', profile.id)
          .eq('sku', sku)
          .maybeSingle();

        let result;
        if (existing?.id) {
          result = await supabase
            .from('amazon_products')
            .update({ ...payload, updated_at: now })
            .eq('id', existing.id)
            .select()
            .single();
        } else {
          result = await supabase
            .from('amazon_products')
            .insert([payload])
            .select()
            .single();
        }

        if (result.error) {
          errors.push({ row: __rowNumber, error: result.error.message });
        } else {
          imported += 1;
        }
      } catch (err) {
        errors.push({ row: __rowNumber, error: err.message });
      }
    }

    await loadProducts();
    return { success: true, imported, errors };
  };

  return (
    <AmazonProductsContext.Provider value={{ products, loading, error, loadProducts, saveProduct, deleteProduct, bulkImportProducts }}>
      {children}
    </AmazonProductsContext.Provider>
  );
};
